import React, {useLayoutEffect, useRef, useEffect} from 'react'
import * as am5 from '@amcharts/amcharts5'
import * as am5map from '@amcharts/amcharts5/map'
import am5geodata_usaLow from '@amcharts/amcharts5-geodata/usaLow'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import {PointItem} from '../../definitions'

const MAP_ID = 'shipbae-map-view'

type MapViewProps = {
  points: PointItem[]
  selected?: string | null
  id?: string
  size?: 'sm' | 'md' | 'lg'
  zoomable?: boolean
}

const SIZE_MAP = {
  sm: {width: '200px', height: '140px'},
  md: {width: '360px', height: '210px'},
  lg: {width: '480px', height: '300px'},
} as const

const POINT_STYLE = {
  sm: {
    big: 14,
    mid: 12,
    small: 10,
    strokeW: 2,
    font: 10,
  },
  md: {
    big: 24,
    mid: 22,
    small: 20,
    strokeW: 4,
    font: 14,
  },
  lg: {
    big: 32,
    mid: 30,
    small: 28,
    strokeW: 5,
    font: 18,
  },
} as const

export const MapView = ({
  points,
  selected,
  id = MAP_ID,
  size = 'md',
  zoomable = true,
}: MapViewProps) => {
  const rootRef = useRef<am5.Root | null>(null)
  const pointSeriesRef = useRef<am5map.MapPointSeries | null>(null)

  // 1️⃣ Create chart once
  useLayoutEffect(() => {
    const root = am5.Root.new(id)
    rootRef.current = root
    root._logo?.dispose()

    root.setThemes([am5themes_Animated.new(root)])

    const style = POINT_STYLE[size]

    const chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: zoomable ? 'rotateX' : 'none',
        panY: zoomable ? 'translateY' : 'none',
        projection: am5map.geoAlbersUsa(),
        homeGeoPoint: {longitude: -95.7129, latitude: 37.0902},
        homeZoomLevel: 3.5,
        maxZoomLevel: zoomable ? 3 : 3.5,
        minZoomLevel: zoomable ? 0.7 : 3.5,
        wheelX: zoomable ? 'rotateX' : 'none',
        wheelY: zoomable ? 'zoom' : 'none',
        pinchZoom: zoomable,
      })
    )

    const contiguousUSA: GeoJSON.GeoJSON = {
      type: 'FeatureCollection',
      features: am5geodata_usaLow.features.filter(
        (f) => f.properties?.name !== 'Alaska' && f.properties?.name !== 'Hawaii' && f.geometry
      ),
    }

    const polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {geoJSON: contiguousUSA})
    )
    polygonSeries.mapPolygons.template.setAll({
      fill: am5.color(0xacc5bf),
      stroke: am5.color(0xffffff),
      strokeWidth: 2,
      strokeDasharray: [4, 8],
    })

    const pointSeries = chart.series.push(am5map.MapPointSeries.new(root, {}))
    pointSeriesRef.current = pointSeries

    // --- bullets (updated with size)
    pointSeries.bullets.push((root, series, dataItem) => {
      const ctx = dataItem.dataContext as PointItem

      const container = am5.Container.new(root, {})

      // highlighted circle (selected)
      const bigCircle = am5.Circle.new(root, {
        radius: style.big,
        fillOpacity: 0,
        stroke: am5.color(0x14b8a6),
        strokeOpacity: 0,
        strokeWidth: style.strokeW,
      })
      container.children.push(bigCircle)

      // middle container
      const middle = am5.Container.new(root, {})

      middle.children.push(am5.Circle.new(root, {radius: style.mid, fillOpacity: 0}))

      // main small circle
      container.children.push(
        am5.Circle.new(root, {radius: style.small, fill: am5.color(0x14b8a6)})
      )

      // label
      if (size !== 'sm') {
        const label = am5.Label.new(root, {
          text: ctx.title,
          fill: am5.color(0xffffff),
          fontSize: style.font,
          fontWeight: 'bold',
          centerX: am5.p50,
          centerY: am5.p50,
        })
        middle.children.push(label)
      }

      container.children.push(middle)

      return am5.Bullet.new(root, {sprite: container})
    })

    chart.appear(1000, 100)

    return () => {
      root.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, zoomable])

  // 3️⃣ Update points data + apply selected after bullets created
  useEffect(() => {
    if (!pointSeriesRef.current) return

    pointSeriesRef.current.data.setAll(points)

    // apply selected after points rendered
    requestAnimationFrame(() => {
      pointSeriesRef.current?.dataItems.forEach((dataItem) => {
        const ctx = dataItem.dataContext as PointItem
        const container = dataItem.bullets?.[0]?.get('sprite') as am5.Container
        if (!container) return

        const bigCircle = container.children.getIndex(0) as am5.Circle
        if (selected === ctx.title) {
          bigCircle.set('strokeOpacity', 0.4)
          bigCircle.set('visible', true)
        } else {
          bigCircle.set('strokeOpacity', 0)
          bigCircle.set('visible', false)
        }
      })
    })
  }, [points, selected])

  // 4️⃣ Update selected
  useEffect(() => {
    if (!pointSeriesRef.current) return

    pointSeriesRef.current.dataItems.forEach((dataItem) => {
      const ctx = dataItem.dataContext as PointItem

      const container = dataItem.bullets?.[0]?.get('sprite') as am5.Container
      if (!container) return

      const bigCircle = container.children.getIndex(0) as am5.Circle
      if (!bigCircle) return

      if (selected === ctx.title) {
        bigCircle.set('strokeOpacity', 0.4)
        bigCircle.set('visible', true)
      } else {
        bigCircle.set('strokeOpacity', 0)
        bigCircle.set('visible', false)
      }
    })
  }, [selected])

  return <div id={id} style={SIZE_MAP[size]} />
}
